import React, { FC, memo, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { ExclamationCircleOutlined } from '@ant-design/icons/lib';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { WarningOutlined } from '@ant-design/icons';
import { BusinessPartner } from 'models/businessPartner';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface BusinessPartnerDeleteButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartner: BusinessPartner;
  tooltip?: ReactNode;
  onStatusChange?: () => void;
  actionType: 'activate' | 'deactivate';
}

export const BusinessPartnerActivateButton: FC<BusinessPartnerDeleteButtonProps> = memo(
  ({
    actionType,
    businessPartner,
    onStatusChange,
    tooltip,
    ...buttonProps
  }) => {
    const [mutate, { isLoading }] =
      actionType === 'activate'
        ? useMutation(BusinessPartnersAPI.activatePartner)
        : useMutation(BusinessPartnersAPI.deactivatePartner);
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title: intlMessage(
          {
            id:
              actionType === 'activate'
                ? 'businessPartners.activate.confirmation'
                : 'businessPartners.deactivate.confirmation',
          },
          {
            name: businessPartner.name,
          }
        ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(businessPartner.id, {
            onSuccess: () => {
              message.success(
                intlMessage(
                  {
                    id:
                      actionType === 'activate'
                        ? 'user.activated'
                        : 'user.deactivated',
                  },
                  {
                    name: businessPartner.name,
                  }
                )
              );
              onStatusChange && onStatusChange();
            },
          }),
      });

    const button = (
      <Button
        id={
          actionType === 'activate'
            ? 'button-activate-business-partner'
            : 'button-deactivate-business-partner'
        }
        loading={isLoading}
        onClick={() => confirm()}
        icon={<WarningOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
